import { FormControl, FormGroup, SxProps } from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import clsx from "clsx";
import {
  ADDITIONAL_FACILITY_INDEXES,
  ADMISSION_POSSIBLE,
  GUARDIAN,
  MAX_REHAB_FACILITIES,
  PREDICAMENT_STATE_NO,
  PREDICAMENT_STATE_YES,
  SCHEMA_VERSION,
} from "core/consts";
import { GUARDIAN_1, GUARDIAN_2 } from "core/schemas/GKVGeneralFormSchema";
import { Auction, SchemaVersion } from "core/types";
import CheckboxInputField from "ds_legacy/components/CheckboxInputField";
import { SubheadingFormLegend } from "ds_legacy/components/FormComponents/SubheadingFormLegend";
import RSButton from "ds_legacy/components/RSButton";
import RadioGroupV2 from "ds_legacy/components/RadioGroupV2";
import {
  CUSTOM_BLACK,
  RADIO_WHITE_BACKGROUND,
} from "ds_legacy/materials/colors";
import { VerticalLayout } from "ds_legacy/materials/layouts";
import {
  important,
  margin,
  padding,
  sizing,
} from "ds_legacy/materials/metrics";
import {
  Body,
  FONT_SIZE_14,
  FONT_SIZE_16,
  FONT_SIZE_18,
  FONT_WEIGHT_BOLD,
  Subheading,
} from "ds_legacy/materials/typography";
import {
  DatePickerWithLabel,
  FormSectionsWrapper,
  PatientFormSubheading,
  PatientFormsSectionHeader,
  SMALL_INPUT_MIN_WIDTH,
  SMALL_INPUT_WIDTH,
  Section,
  SectionRow,
  TextAreaWithLabel,
  TextInputWithLabel,
  YesNoRadioWithLabel,
  patientSubheadingSx,
} from "dsl/ecosystems/PatientForms/components";
import { DownloadIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { FormElementRenderProps, FormWatcher } from "react-forms-state/src";
import { useStateDispatcherContext } from "react-forms-state/src/Context";
import { useTranslations } from "translations";
import { useGuardianOptions, useLivingSituationOptions } from "./hooks";

export const parseAddress = (addressString: string | null) => {
  if (!addressString) return { houseNumber: null, street: null };

  const match = addressString.match(/^(.+?)\s+(\d+.*)$/);
  if (match) {
    return { houseNumber: match[2].trim(), street: match[1].trim() };
  }
  return { houseNumber: null, street: addressString };
};

function FacilityFields({
  auction,
  facilityIndex,
  fieldPrefix,
  handleProviderImport,
  isRequired,
  isV2,
  showImportButton,
}: {
  fieldPrefix: string;
  isRequired: boolean;
  showImportButton?: boolean;
  auction?: Auction;
  handleProviderImport?: () => void;
  isV2: boolean;
  facilityIndex: number;
}) {
  const translations = useTranslations();

  return (
    <div className="space-y-4" data-testid={`rehab_facility_${facilityIndex}`}>
      {showImportButton && auction?.winner && handleProviderImport && (
        <RSButton
          color="primary"
          id="import-provider-from-recare"
          LeftIcon={DownloadIcon}
          loading="na"
          variant="outlined"
          onClick={handleProviderImport}
        >
          {translations.patientForms.generalForm.importProviderInformation}
        </RSButton>
      )}
      <div className="flex space-x-8">
        <TextInputWithLabel
          elementName={`${fieldPrefix}name`}
          isBold
          isLarge
          label={translations.institution.name}
          required={isRequired}
          style={{ margin: margin(0) }}
          withFlatModel
        />
      </div>
      <div className="flex space-x-8">
        <TextInputWithLabel
          elementName={`${fieldPrefix}street`}
          isBold
          isLarge
          label={translations.address.street}
          required={isRequired}
          style={{ margin: margin(0) }}
          withFlatModel
        />
        <TextInputWithLabel
          elementName={`${fieldPrefix}house_number`}
          isBold
          label={translations.address.number}
          required={isRequired}
          style={{ margin: margin(0) }}
          withFlatModel
        />
      </div>
      <div className="flex space-x-8">
        <TextInputWithLabel
          elementName={`${fieldPrefix}postal_code`}
          isBold
          label={translations.address.zipcode}
          required={isRequired}
          style={{ margin: margin(0) }}
          withFlatModel
        />
        <TextInputWithLabel
          elementName={`${fieldPrefix}city`}
          isBold
          isLarge
          label={translations.address.city}
          required={isRequired}
          style={{ margin: margin(0) }}
          withFlatModel
        />
      </div>
      <div className="flex space-x-8">
        <TextInputWithLabel
          elementName={`${fieldPrefix}phone`}
          isBold
          isLarge
          label={translations.contactInformation.phoneNumber}
          required={isRequired}
          style={{ margin: margin(0) }}
          withFlatModel
        />
      </div>
      {isV2 && (
        <RadioGroupV2
          elementName={`${fieldPrefix}admission_possible`}
          formLabelSx={{
            color: important(CUSTOM_BLACK),
            fontSize: FONT_SIZE_14,
            fontWeight: FONT_WEIGHT_BOLD,
          }}
          isRequired={isRequired}
          label={
            translations.patientForms.generalForm.sectionTwo.admissionPossible
          }
          options={[
            {
              label:
                translations.patientForms.generalForm.sectionTwo
                  .admissionPossibleYes,
              value: ADMISSION_POSSIBLE.YES,
            },
            {
              label:
                translations.patientForms.generalForm.sectionTwo
                  .admissionPossibleNo,
              value: ADMISSION_POSSIBLE.NO,
            },
            {
              label:
                translations.patientForms.generalForm.sectionTwo
                  .admissionPossibleNoAnswer,
              value: ADMISSION_POSSIBLE.NO_ANSWER,
            },
          ]}
          radioSx={RADIO_WHITE_BACKGROUND}
          withFlatModel
        />
      )}
      <div className="flex space-x-8">
        <DatePickerWithLabel
          elementName={`${fieldPrefix}contacted_start_date`}
          inputSx={{ width: SMALL_INPUT_WIDTH }}
          isBold
          isRequired={isRequired}
          label={
            translations.patientForms.generalForm.sectionTwo.rehabIntakeDate
          }
          withFlatModel
          wrapperSx={{ width: "100%" }}
        />
      </div>
    </div>
  );
}

function RehabFacilitiesSubForm({
  auction,
  handleProviderImport,
  isV2,
  shouldSkipRequiredValidation,
}: {
  auction?: Auction;
  handleProviderImport: () => void;
  isV2: boolean;
  shouldSkipRequiredValidation: boolean;
}) {
  const translations = useTranslations();
  const { onStateChange } = useStateDispatcherContext();
  const [facilityCount, setFacilityCount] = useState(1);
  const initializedRef = useRef(false);

  useEffect(() => {
    if (initializedRef.current) {
      onStateChange?.(facilityCount, "prefered_clinic_count", null);
    }
  }, [facilityCount, onStateChange]);

  useEffect(() => {
    initializedRef.current = true;
  }, []);

  const clearFacilityFields = (idx: number) => {
    onStateChange?.(null, `prefered_clinic_${idx}_name`, null);
    onStateChange?.(null, `prefered_clinic_${idx}_street`, null);
    onStateChange?.(null, `prefered_clinic_${idx}_house_number`, null);
    onStateChange?.(null, `prefered_clinic_${idx}_postal_code`, null);
    onStateChange?.(null, `prefered_clinic_${idx}_city`, null);
    onStateChange?.(null, `prefered_clinic_${idx}_phone`, null);
    onStateChange?.(null, `prefered_clinic_${idx}_contacted_start_date`, null);
    onStateChange?.(null, `prefered_clinic_${idx}_admission_possible`, null);
  };

  const additionalIndexes = ADDITIONAL_FACILITY_INDEXES.filter(
    (idx) => idx <= facilityCount,
  );

  return (
    <div className="ml-8 space-y-4 py-4">
      {isV2 &&
        ADDITIONAL_FACILITY_INDEXES.map((idx) => (
          <FormElementRenderProps
            key={`detect-${idx}`}
            elementName={`prefered_clinic_${idx}_name`}
            withFlatModel
          >
            {({ value }) => {
              if (value && typeof value === "string" && value.trim() !== "") {
                if (idx > facilityCount) {
                  // Use queueMicrotask to safely update state outside render
                  queueMicrotask(() => {
                    setFacilityCount((prev) => {
                      const next = Math.max(prev, idx);
                      if (next !== prev) {
                        initializedRef.current = true;
                      }
                      return next;
                    });
                  });
                }
              }
              return <></>;
            }}
          </FormElementRenderProps>
        ))}

      <FacilityFields
        auction={auction}
        facilityIndex={1}
        fieldPrefix="prefered_clinic_"
        handleProviderImport={handleProviderImport}
        isRequired={!shouldSkipRequiredValidation}
        isV2={isV2}
        showImportButton
      />

      {isV2 && (
        <>
          {additionalIndexes.map((idx) => (
            <div key={idx} className="space-y-4 border-t pt-4">
              <div className="flex items-center justify-between">
                <Body
                  style={{
                    fontSize: FONT_SIZE_14,
                    fontWeight: FONT_WEIGHT_BOLD,
                  }}
                >
                  {translations.patientForms.generalForm.sectionTwo.facility}{" "}
                  {idx}
                </Body>
                {idx === facilityCount && (
                  <RSButton
                    color="error"
                    id={`remove-facility-${idx}`}
                    loading="na"
                    variant="text"
                    onClick={() => {
                      clearFacilityFields(idx);
                      setFacilityCount((prev) => prev - 1);
                    }}
                  >
                    {
                      translations.patientForms.generalForm.sectionTwo
                        .removeFacility
                    }
                  </RSButton>
                )}
              </div>
              <FacilityFields
                facilityIndex={idx}
                fieldPrefix={`prefered_clinic_${idx}_`}
                isRequired={!shouldSkipRequiredValidation}
                isV2={isV2}
              />
            </div>
          ))}

          {facilityCount < MAX_REHAB_FACILITIES && (
            <RSButton
              color="primary"
              id="add-another-facility"
              loading="na"
              variant="outlined"
              onClick={() => setFacilityCount((prev) => prev + 1)}
            >
              {
                translations.patientForms.generalForm.sectionTwo
                  .addAnotherFacility
              }
            </RSButton>
          )}
        </>
      )}
    </div>
  );
}

export function GKVGeneralFormPresenter({
  auction,
  schemaVersion,
  shouldSkipRequiredValidation,
}: {
  auction?: Auction;
  schemaVersion?: SchemaVersion;
  shouldSkipRequiredValidation: boolean;
}) {
  const translations = useTranslations();
  const livingSituationOptions = useLivingSituationOptions();
  const allGuardianOptions = useGuardianOptions();
  // v2.0: show all 4 guardian types including "Ehegattennotvertretung"
  // v1.1.0: only show 3 guardian types (exclude spousal emergency representative)
  const guardianOptions =
    schemaVersion === SCHEMA_VERSION.V2_0_0
      ? allGuardianOptions
      : allGuardianOptions.filter(
          (opt) => opt.value !== GUARDIAN.SPOUSAL_EMERGENCY_REP,
        );
  const { onStateChange } = useStateDispatcherContext();

  const handleProviderImport = () => {
    if (!auction?.winner?.careprovider || !onStateChange) return;

    const { careprovider } = auction.winner;
    const { address, central_phone_number, name } = careprovider;

    const { houseNumber, street } = address?.street
      ? { houseNumber: address.street_number, street: address.street }
      : parseAddress(address?.address ?? null);

    onStateChange(name ?? null, "prefered_clinic_name", null);
    onStateChange(street ?? null, "prefered_clinic_street", null);
    onStateChange(houseNumber ?? null, "prefered_clinic_house_number", null);
    onStateChange(
      address?.zip_code ?? null,
      "prefered_clinic_postal_code",
      null,
    );
    onStateChange(address?.city ?? null, "prefered_clinic_city", null);
    onStateChange(central_phone_number ?? null, "prefered_clinic_phone", null);
    onStateChange(
      auction.start_date ?? null,
      "prefered_clinic_contacted_start_date",
      null,
    );
  };

  return (
    <VerticalLayout width="100%">
      <FormElementRenderProps elementName="hospital_ik_number">
        {({ value }) => (
          <input name="hospital_ik_number" type="hidden" value={value ?? ""} />
        )}
      </FormElementRenderProps>
      <FormElementRenderProps elementName="insurance_ik_number">
        {({ value }) => (
          <input name="insurance_ik_number" type="hidden" value={value ?? ""} />
        )}
      </FormElementRenderProps>
      <FormSectionsWrapper>
        <Section>
          <SectionRow>
            <TextInputWithLabel
              elementName="patient_first_name"
              isBold
              isLarge
              label={translations.people.firstName}
              required={!shouldSkipRequiredValidation}
            />
            <TextInputWithLabel
              elementName="patient_last_name"
              isBold
              isLarge
              label={translations.people.lastName}
              required={!shouldSkipRequiredValidation}
            />
          </SectionRow>
          {schemaVersion === SCHEMA_VERSION.V2_0_0 ? (
            <FormWatcher watchPath="patient_street">
              {({ watchedValue: streetValue }) => (
                <FormWatcher watchPath="patient_postfach">
                  {({ watchedValue: postfachValue }) => {
                    const streetFilled =
                      typeof streetValue === "string" &&
                      streetValue.trim().length > 0;
                    const postfachFilled =
                      typeof postfachValue === "string" &&
                      postfachValue.trim().length > 0;
                    return (
                      <SectionRow>
                        <TextInputWithLabel
                          elementName="patient_street"
                          isBold
                          isLarge
                          label={translations.address.street}
                          required={
                            !shouldSkipRequiredValidation && !postfachFilled
                          }
                          sideMutation={(_val, mutate) => {
                            mutate(
                              postfachValue ?? null,
                              "patient_postfach",
                              true,
                            );
                          }}
                        />
                        <TextInputWithLabel
                          elementName="patient_housenumber"
                          isBold
                          label={translations.address.number}
                        />
                        <div>{translations.general.or}</div>
                        <TextInputWithLabel
                          elementName="patient_postfach"
                          isBold
                          label={
                            translations.patientForms.generalForm.insuredPerson
                              .postfach
                          }
                          required={
                            !shouldSkipRequiredValidation && !streetFilled
                          }
                          sideMutation={(_val, mutate) => {
                            mutate(streetValue ?? null, "patient_street", true);
                            mutate(null, "patient_housenumber", true);
                          }}
                        />
                      </SectionRow>
                    );
                  }}
                </FormWatcher>
              )}
            </FormWatcher>
          ) : (
            <SectionRow>
              <TextInputWithLabel
                elementName="patient_street"
                isBold
                isLarge
                label={translations.address.street}
              />
              <TextInputWithLabel
                elementName="patient_housenumber"
                isBold
                label={translations.address.number}
              />
            </SectionRow>
          )}
          <SectionRow>
            <TextInputWithLabel
              elementName="patient_zipcode"
              isBold
              label={translations.address.zipcode}
            />
            <TextInputWithLabel
              elementName="patient_city"
              isBold
              isLarge
              label={translations.address.city}
            />
          </SectionRow>
          <SectionRow>
            <TextInputWithLabel
              elementName="patient_phone_number"
              isBold
              isLarge
              label={translations.patientForms.generalForm.insuredPerson.phone}
            />
            <DatePickerWithLabel
              elementName="patient_birthdate"
              isBold
              isRequired={!shouldSkipRequiredValidation}
              label={
                translations.patientForms.generalForm.insuredPerson.dateOfBirth
              }
            />
          </SectionRow>
          <SectionRow>
            <TextInputWithLabel
              elementName="insurance_number"
              isBold
              label={
                translations.patientForms.generalForm.insuredPerson
                  .insuranceNumber
              }
              required={!shouldSkipRequiredValidation}
            />
          </SectionRow>
        </Section>
      </FormSectionsWrapper>
      <FormSectionsWrapper>
        <PatientFormsSectionHeader>
          {translations.patientForms.generalForm.sectionI.title}
        </PatientFormsSectionHeader>
        <Section>
          <Subheading
            as="h5"
            margin={margin(0, 0, 0.5, 0.5)}
            style={{
              fontSize: FONT_SIZE_18,
              fontWeight: FONT_WEIGHT_BOLD,
            }}
          >
            {translations.patientForms.generalForm.sectionI.titleNeedForCare}
          </Subheading>
          <YesNoRadioWithLabel
            elementName="insured_details_application_insurance_benefits"
            isRequired={!shouldSkipRequiredValidation}
            label={
              translations.patientForms.generalForm.sectionI.questionNeedForCare
            }
          />
          <PatientFormSubheading>
            {
              translations.patientForms.generalForm.sectionI
                .titlePensionApplication
            }
          </PatientFormSubheading>
          <YesNoRadioWithLabel
            elementName="retirement_pension_applied"
            isRequired={!shouldSkipRequiredValidation}
            label={
              translations.patientForms.generalForm.sectionI
                .subtitleRetirementPension
            }
          />
          <TextAreaWithLabel
            elementName="retirement_pension_company"
            label={
              translations.patientForms.generalForm.sectionI
                .placeholderLabelInsuranceName
            }
            sx={{ margin: margin(0) }}
          />
          <YesNoRadioWithLabel
            elementName="retirement_pension_earning_capacity_applied"
            formLabelSx={{ padding: padding(2, 0, 1) }}
            isRequired={!shouldSkipRequiredValidation}
            label={
              translations.patientForms.generalForm.sectionI
                .subtitlePensionReducedEarning
            }
          />
          <TextAreaWithLabel
            elementName="retirement_earning_capacity_company"
            label={
              translations.patientForms.generalForm.sectionI
                .placeholderLabelInsuranceName
            }
            sx={{ margin: margin(0) }}
          />
          <PatientFormSubheading withBottomPadding>
            {
              translations.patientForms.generalForm.sectionI
                .questionLivingSituation
            }
          </PatientFormSubheading>
          <TextInputWithLabel
            elementName="living_lift_description"
            label={
              translations.patientForms.generalForm.sectionI
                .livingSituationFloor
            }
            labelWrapperSx={{ width: "100%" }}
            required={!shouldSkipRequiredValidation}
            style={{
              margin: margin(0),
              minWidth: SMALL_INPUT_MIN_WIDTH,
              width: SMALL_INPUT_WIDTH,
            }}
          />
          <YesNoRadioWithLabel
            elementName="living_lift"
            isRequired={!shouldSkipRequiredValidation}
            label={
              translations.patientForms.generalForm.sectionI
                .livingSituationElevator
            }
          />
          <TextAreaWithLabel
            elementName="living_special"
            label={
              translations.patientForms.generalForm.sectionI
                .livingSituationSpecifications
            }
            sx={{ margin: margin(0) }}
          />
          <PatientFormSubheading>
            {translations.patientForms.generalForm.sectionI.questionHomeCare}
          </PatientFormSubheading>
          <RadioGroupV2
            elementName="living_situation"
            formControlLabelSx={{ marginLeft: 0, paddingBottom: sizing(0.5) }}
            formLabelSx={{
              fontSize: FONT_SIZE_14,
              padding: padding(1, 0),
            }}
            isRequired={!shouldSkipRequiredValidation}
            label={
              translations.patientForms.generalForm.sectionI.subtitleHomeCare
            }
            options={livingSituationOptions}
            radioSx={RADIO_WHITE_BACKGROUND}
            sideMutation={(_, mutate) =>
              mutate(null, "living_situation_at_home")
            }
          />
          <YesNoRadioWithLabel
            elementName="domestic_situation_is_safe"
            isRequired={!shouldSkipRequiredValidation}
            label={
              translations.patientForms.generalForm.sectionI
                .currentHomeCareEnsured
            }
          />
          <PatientFormSubheading>
            {translations.patientForms.generalForm.sectionI.questionGp}
          </PatientFormSubheading>
          <SectionRow customPadding={padding(1, 0, 0)}>
            <TextInputWithLabel
              elementName="general_practitioner_honorific_prefix"
              isBold
              isLarge
              label={
                translations.patient.patientInformation
                  .generalPractitionerHonorificPrefix
              }
              placeholder={
                translations.patient.patientInformation
                  .generalPractitionerHonorificPrefixPlaceholder
              }
            />
            <TextInputWithLabel
              elementName="general_practitioner_honorific_suffix"
              isBold
              isLarge
              label={
                translations.patient.patientInformation
                  .generalPractitionerHonorificSuffix
              }
              placeholder={
                translations.patient.patientInformation
                  .generalPractitionerHonorificSuffixPlaceholder
              }
            />
          </SectionRow>
          <SectionRow customPadding={padding(1, 0, 0)}>
            <TextInputWithLabel
              elementName="general_practitioner_first_name"
              isBold
              isLarge
              label={translations.people.firstName}
              placeholder={
                translations.patient.patientInformation
                  .generalPractitionerFirstNamePlaceholder
              }
            />
            <TextInputWithLabel
              elementName="general_practitioner_last_name"
              isBold
              isLarge
              label={translations.people.lastName}
              placeholder={
                translations.patient.patientInformation
                  .generalPractitionerLastNamePlaceholder
              }
            />
          </SectionRow>
          <SectionRow customPadding={padding(1, 0, 0)}>
            <TextInputWithLabel
              elementName="general_practitioner_job_title"
              isBold
              isLarge
              label={
                translations.patient.patientInformation
                  .generalPractitionerJobTitle
              }
              placeholder={
                translations.patient.patientInformation
                  .generalPractitionerJobTitlePlaceholder
              }
            />
          </SectionRow>
          <Body
            as="p"
            fontSize={FONT_SIZE_16}
            fontWeight={FONT_WEIGHT_BOLD}
            margin={margin(3, 0, 0)}
          >
            {translations.patientForms.generalForm.sectionI.subtitleAddressGp}
          </Body>
          <SectionRow customPadding={padding(2, 0, 0)}>
            <TextInputWithLabel
              elementName="general_practitioner_street"
              isBold
              isLarge
              label={translations.address.street}
              placeholder={
                translations.patient.patientInformation
                  .generalPractitionerStreetPlaceholder
              }
            />
            <TextInputWithLabel
              elementName="general_practitioner_housenumber"
              isBold
              label={translations.address.number}
              placeholder={
                translations.patient.patientInformation
                  .generalPractitionerHouseNumberPlaceholder
              }
            />
          </SectionRow>
          <SectionRow>
            <TextInputWithLabel
              elementName="general_practitioner_zipcode"
              isBold
              label={translations.patientForms.generalForm.sectionI.gpZipCode}
              placeholder={
                translations.patient.patientInformation
                  .generalPractitionerZipcodePlaceholder
              }
            />
            <TextInputWithLabel
              elementName="general_practitioner_city"
              isBold
              isLarge
              label={translations.patientForms.generalForm.sectionI.gpCity}
              placeholder={
                translations.patient.patientInformation
                  .generalPractitionerCityPlaceholder
              }
            />
          </SectionRow>
          <SectionRow>
            <TextInputWithLabel
              elementName="general_practitioner_phone_number"
              isBold
              isLarge
              label={
                translations.patientForms.generalForm.sectionI.gpPhoneNumber
              }
              placeholder={
                translations.patient.patientInformation
                  .generalPractitionerPhonePlaceholder
              }
            />
          </SectionRow>
          <PatientFormSubheading>
            {translations.patientForms.generalForm.sectionI.questionGuardian}
          </PatientFormSubheading>
          <div className="flex flex-col space-y-4">
            {[GUARDIAN_1, GUARDIAN_2].map((num) => (
              <div
                key={num}
                className={clsx("flex flex-col", {
                  "pt-4": num === GUARDIAN_2,
                })}
              >
                <RadioGroupV2
                  elementName={`insured_details_guardian_${num}_type`}
                  formControlLabelSx={{
                    marginLeft: 0,
                    paddingBottom: sizing(0.5),
                  }}
                  formLabelSx={visuallyHidden as SxProps}
                  label={
                    translations.patientForms.generalForm.sectionI
                      .accessibilityGuardianType
                  }
                  options={guardianOptions}
                  radioGroupSx={{ flexDirection: "row", paddingTop: sizing(1) }}
                  radioSx={RADIO_WHITE_BACKGROUND}
                />
                <SectionRow customPadding={padding(1, 0, 0)}>
                  <TextInputWithLabel
                    elementName={`insured_details_guardian_${num}_first_name`}
                    isBold
                    isLarge
                    label={translations.people.firstName}
                  />
                  <TextInputWithLabel
                    elementName={`insured_details_guardian_${num}_last_name`}
                    isBold
                    isLarge
                    label={translations.people.lastName}
                  />
                </SectionRow>
                <Body
                  as="p"
                  fontSize={FONT_SIZE_16}
                  fontWeight={FONT_WEIGHT_BOLD}
                  margin={margin(3, 0, 0)}
                >
                  {
                    translations.patientForms.generalForm.sectionI
                      .subtitleAddressGp
                  }
                </Body>
                <SectionRow customPadding={padding(2, 0, 0)}>
                  <TextInputWithLabel
                    elementName={`insured_details_guardian_${num}_street`}
                    isBold
                    isLarge
                    label={translations.address.street}
                  />
                  <TextInputWithLabel
                    elementName={`insured_details_guardian_${num}_house_number`}
                    isBold
                    label={translations.address.number}
                  />
                </SectionRow>
                <SectionRow>
                  <TextInputWithLabel
                    elementName={`insured_details_guardian_${num}_zipcode`}
                    isBold
                    label={
                      translations.patientForms.generalForm.sectionI.gpZipCode
                    }
                  />
                  <TextInputWithLabel
                    elementName={`insured_details_guardian_${num}_city`}
                    isBold
                    isLarge
                    label={
                      translations.patientForms.generalForm.sectionI.gpCity
                    }
                  />
                </SectionRow>
                <SectionRow>
                  <TextInputWithLabel
                    elementName={`insured_details_guardian_${num}_phone`}
                    isBold
                    isLarge
                    label={
                      translations.patientForms.generalForm.sectionI
                        .gpPhoneNumber
                    }
                  />
                </SectionRow>
              </div>
            ))}
          </div>
          <PatientFormSubheading withBottomPadding>
            {
              translations.patientForms.generalForm.sectionI
                .questionSpecialRequirements
            }
          </PatientFormSubheading>
          <TextAreaWithLabel
            elementName="reason_for_reha_clinic"
            label={
              translations.patientForms.generalForm.sectionI
                .labelSpecialRequirements
            }
            sx={{ margin: margin(0) }}
          />
        </Section>
        <Section>
          <PatientFormSubheading withBottomPadding>
            {translations.patientForms.generalForm.sectionI.additionalInfoNote}
          </PatientFormSubheading>
          <FormControl component="fieldset">
            <SubheadingFormLegend>
              {
                translations.patientForms.generalForm.sectionI
                  .signatureDeclaration
              }
            </SubheadingFormLegend>
            <FormGroup>
              <CheckboxInputField
                customValue={String(PREDICAMENT_STATE_YES)}
                elementName="declaration_of_intent_confirmed"
                label={
                  translations.patientForms.generalForm
                    .declarationOfIntentProvided
                }
                required={!shouldSkipRequiredValidation}
                withFlatModel
              />
            </FormGroup>
          </FormControl>
        </Section>
        <Section>
          <div className="mb-4 flex flex-col space-y-2">
            <PatientFormSubheading>
              {
                translations.patientForms.generalForm.sectionI
                  .consentToDataTransferHeading
              }
            </PatientFormSubheading>
            <p>
              {
                translations.patientForms.generalForm.sectionI
                  .consentToDataTransferText
              }
            </p>
          </div>
          <RadioGroupV2
            elementName="data_transfer_consent_given"
            formControlLabelSx={{ margin: margin(0, 0, 0, 0) }}
            formLabelSx={{
              fontWeight: FONT_WEIGHT_BOLD,
              margin: margin(0, 0, 0, 0.5),
            }}
            isRequired={!shouldSkipRequiredValidation}
            label={
              translations.patientForms.generalForm.sectionI
                .consentToDataTransferSignature
            }
            options={[
              {
                label:
                  translations.patientForms.generalForm.consentDataProvided,
                value: PREDICAMENT_STATE_YES,
              },
              {
                label:
                  translations.patientForms.generalForm.sectionTwo
                    .consentDataNotProvided,
                value: PREDICAMENT_STATE_NO,
              },
            ]}
          />
        </Section>
      </FormSectionsWrapper>
      <FormSectionsWrapper>
        <PatientFormsSectionHeader>
          {translations.patientForms.generalForm.sectionTwo.title}
        </PatientFormsSectionHeader>
        <Section>
          <SectionRow>
            <TextInputWithLabel
              disabled
              elementName="site_id_number"
              isBold
              isLarge
              label={translations.careseeker.siteIdNumber}
              required={!shouldSkipRequiredValidation}
            />
            <TextInputWithLabel
              elementName="case_id"
              isBold
              isLarge
              label={translations.patientForms.generalForm.sectionTwo.patientId}
              required={!shouldSkipRequiredValidation}
            />
          </SectionRow>
          {schemaVersion === SCHEMA_VERSION.V2_0_0 && (
            <>
              <PatientFormSubheading>
                {translations.patientForms.generalForm.sectionTwo.hospitalName}
              </PatientFormSubheading>
              <div className="flex space-x-8">
                <TextInputWithLabel
                  elementName="hospital_name"
                  isBold
                  isLarge
                  label={translations.institution.name}
                  required={!shouldSkipRequiredValidation}
                  style={{ margin: margin(0) }}
                />
              </div>
              <div className="flex space-x-8">
                <TextInputWithLabel
                  elementName="hospital_street"
                  isBold
                  isLarge
                  label={translations.address.street}
                  required={!shouldSkipRequiredValidation}
                  style={{ margin: margin(0) }}
                />
                <TextInputWithLabel
                  elementName="hospital_house_number"
                  isBold
                  label={translations.address.number}
                  style={{ margin: margin(0) }}
                />
              </div>
              <div className="flex space-x-8">
                <TextInputWithLabel
                  elementName="hospital_postal_code"
                  isBold
                  label={translations.address.zipcode}
                  required={!shouldSkipRequiredValidation}
                  style={{ margin: margin(0) }}
                />
                <TextInputWithLabel
                  elementName="hospital_city"
                  isBold
                  isLarge
                  label={translations.address.city}
                  required={!shouldSkipRequiredValidation}
                  style={{ margin: margin(0) }}
                />
              </div>
            </>
          )}
          <PatientFormSubheading>
            {
              translations.patientForms.generalForm.sectionTwo
                .detailsSocialWorker
            }
          </PatientFormSubheading>
          <SectionRow customPadding={padding(1, 0, 0)}>
            <TextInputWithLabel
              elementName="social_worker_first_name"
              isBold
              isLarge
              label={translations.people.firstName}
            />
            <TextInputWithLabel
              elementName="social_worker_last_name"
              isBold
              isLarge
              label={translations.people.lastName}
              required={!shouldSkipRequiredValidation}
            />
          </SectionRow>
          <SectionRow>
            <TextInputWithLabel
              elementName="social_worker_phone_number"
              isBold
              isLarge
              label={
                translations.patientForms.generalForm.sectionTwo
                  .socialWorkerPhone
              }
              required={!shouldSkipRequiredValidation}
            />
            <TextInputWithLabel
              elementName="social_worker_fax_number"
              isBold
              isLarge
              label={
                translations.patientForms.generalForm.sectionTwo.socialWorkerFax
              }
            />
          </SectionRow>
          <YesNoRadioWithLabel
            elementName="communication_in_german_possible"
            formLabelSx={patientSubheadingSx}
            isRequired={!shouldSkipRequiredValidation}
            label={
              translations.patientForms.generalForm.sectionTwo
                .communicationWithPatient
            }
            sideMutation={(_value, mutate) => {
              mutate(null, "communication_other_language");
            }}
            subForms={{
              [PREDICAMENT_STATE_NO]: (
                <TextAreaWithLabel
                  elementName="communication_other_language"
                  label={
                    translations.patientForms.generalForm.sectionTwo
                      .communicationNoGerman
                  }
                  sx={{ margin: margin(0) }}
                  withFlatModel
                />
              ),
            }}
          />
          <YesNoRadioWithLabel
            elementName="prefered_clinic_contacted"
            formLabelSx={patientSubheadingSx}
            isRequired={!shouldSkipRequiredValidation}
            label={
              translations.patientForms.generalForm.sectionTwo.costCoverage
            }
            shouldStartWithNo
            sideMutation={(_value, mutate) => {
              mutate(null, "prefered_clinic_name");
              mutate(null, "prefered_clinic_street");
              mutate(null, "prefered_clinic_house_number");
              mutate(null, "prefered_clinic_postal_code");
              mutate(null, "prefered_clinic_city");
              mutate(null, "prefered_clinic_phone");
              mutate(null, "prefered_clinic_contacted_start_date");
              mutate(null, "prefered_clinic_admission_possible");
              mutate(null, "prefered_clinic_count");
              // Clear additional facilities
              for (const idx of ADDITIONAL_FACILITY_INDEXES) {
                mutate(null, `prefered_clinic_${idx}_name`);
                mutate(null, `prefered_clinic_${idx}_street`);
                mutate(null, `prefered_clinic_${idx}_house_number`);
                mutate(null, `prefered_clinic_${idx}_postal_code`);
                mutate(null, `prefered_clinic_${idx}_city`);
                mutate(null, `prefered_clinic_${idx}_phone`);
                mutate(null, `prefered_clinic_${idx}_contacted_start_date`);
                mutate(null, `prefered_clinic_${idx}_admission_possible`);
              }
            }}
            subForms={{
              [PREDICAMENT_STATE_YES]: (
                <RehabFacilitiesSubForm
                  auction={auction}
                  handleProviderImport={handleProviderImport}
                  isV2={schemaVersion === SCHEMA_VERSION.V2_0_0}
                  shouldSkipRequiredValidation={shouldSkipRequiredValidation}
                />
              ),
            }}
            withFlatModel
          />
        </Section>
      </FormSectionsWrapper>
    </VerticalLayout>
  );
}
